import React, { useState } from "react";

import { Row, Table, Typography } from "antd";

import usePageLoading from "hooks/usePageLoading";

import { SlowStateBanner, TableSkeleton } from "js/components/Loading";
import { PaginationTable } from "js/components/types";
import { Button as RetryButton } from "js/components/ui/button";

import { useGetPaymentHistory } from "../../../hooks/usePayments";

const { Title } = Typography;

const PaymentHistorySection: React.FC = () => {
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  const {
    data: paymentHistoryData,
    isLoading,
    refetch,
  } = useGetPaymentHistory(pagination);

  const paymentHistoryColumns = [
    {
      title: "Fecha",
      dataIndex: "transaction_date",
      key: "transaction_date",
      width: "15%",
      render: (transaction_date: string) =>
        new Date(transaction_date).toLocaleDateString("es-ES", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      align: "center",
    },
    {
      title: "Movimiento",
      dataIndex: "description",
      key: "description",
      width: "15%",
    },
    {
      title: "Tarjeta",
      dataIndex: "card",
      key: "card",
      render: (card: string) => {
        return `**** **** **** ${card}`;
      },
      width: "15%",
      align: "center",
    },
    {
      title: "Fase",
      dataIndex: "status",
      key: "status",
      width: "15%",
      align: "center",
    },
    {
      title: "Total",
      dataIndex: "amount",
      key: "amount",
      render: (amount: string) => {
        return `$${amount}`;
      },
      width: "15%",
      align: "center",
    },
  ];

  const handleTableChange = (pagination: PaginationTable) => {
    setPagination(pagination);
  };

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void refetch();
  };

  return (
    <>
      <Title level={2}>Historial de Pagos</Title>
      {showSlowState && (
        <SlowStateBanner
          actions={
            <RetryButton
              size="sm"
              variant="outline"
              className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
              onClick={handleRetryFetch}
            >
              Reintentar
            </RetryButton>
          }
        >
          <p className="font-medium">Esto está tardando más de lo previsto.</p>
          <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
        </SlowStateBanner>
      )}
      {showSkeleton ? (
        <div className="mt-4">
          <TableSkeleton columns={5} rows={pagination.pageSize ?? 5} />
        </div>
      ) : (
        <Row gutter={[10, 10]} justify={"start"}>
          <Table
            style={{ overflowX: "auto" }}
            size={"small"}
            columns={paymentHistoryColumns}
            rowKey={(record) => record.id ?? ""}
            dataSource={paymentHistoryData}
            onChange={handleTableChange}
            pagination={pagination}
            scroll={{ y: 750, x: "max-content" }}
            components={{
              header: {
                cell: (props: any) => (
                  <th
                    {...props}
                    style={{
                      textAlign:
                        props.children.includes("Fecha") ||
                        props.children.includes("Tarjeta") ||
                        props.children.includes("Fase") ||
                        props.children.includes("Total")
                          ? "center"
                          : undefined,
                    }}
                  />
                ),
              },
            }}
          />
        </Row>
      )}
    </>
  );
};

export default PaymentHistorySection;
